/**
 * Turn a selection into pages.
 *
 * WHY IT IS ITS OWN COMPONENT. This dialog was ~175 lines welded into
 * LinkedKeywordsPane, reachable only from the keyword list — so the link map,
 * which is the screen that actually shows you which nodes have no page, had no
 * way to make one. Copying it would have been the fourth duplication this
 * session; moving it costs the same and leaves one dialog.
 *
 * The caller owns what happens AFTER: the keyword list refreshes and highlights
 * the new rows, the map just refetches. So this reports the result and stops.
 *
 * `target` is the two ways a run names its subject. Explicit ids are the normal
 * case; a filter is "everything matching", which cannot be enumerated client
 * side because the list is paged — the server resolves it. Both existed before;
 * only the ids path is reachable from the map, which has no filters.
 */
import { useEffect, useState } from 'react';
import Modal from '../Modal';
import PathChips from './PathChips';
import { isNestedSiteType } from './pathRules';
import api, { NULL_REF_ID } from '../../services/api';
import { RUN_FORMATS, runButtonLabel, runOutcome, runTitle } from './runFormats';
import type { DeploymentPlan, ManagedSite, PageStyle } from '../../types';

const PAGE_STYLES: { value: PageStyle; label: string }[] = [
  { value: 'ARTICLE', label: 'Article' },
  { value: 'GUIDE', label: 'Guide' },
  { value: 'CHECKLIST', label: 'Checklist' },
  { value: 'LISTICLE', label: 'Listicle' },
  { value: 'FAQ', label: 'FAQ' },
  { value: 'COMPARISON', label: 'Comparison' },
  { value: 'REVIEWS', label: 'Reviews' },
  { value: 'PRODUCT_PAGE', label: 'Product Page' },
  { value: 'PRICING_PAGE', label: 'Pricing Page' },
  { value: 'SUPPORT_ARTICLE', label: 'Support Article' },
  { value: 'DOCUMENTATION', label: 'Documentation' },
  { value: 'TOOL', label: 'Tool' },
  { value: 'FORUM_DISCUSSION', label: 'Forum Discussion' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'TEMPLATE', label: 'Template' },
];

export interface MakePagesTarget {
  kind: 'ids';
  ids: number[];
}

export interface MakePagesFilterTarget {
  kind: 'filter';
  topicId: number;
  keywordSearch?: string;
  semanticSearch?: string;
  minSemSimilarity?: number;
}

export interface MakePagesResult {
  created_count: number;
  pages: { topic_keyword_id: number }[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  target: MakePagesTarget | MakePagesFilterTarget;
  /** How many things the run is over — drives every count and the button. */
  selectedCount: number;
  /** Where the dialog was opened from, shown under the title. */
  scopeLabel?: string;
  /**
   * The node the run is coming FROM, used to seed the publish path.
   *
   * Separate from `target` because a filter run already names a topic while an
   * ids run does not, and both want the same seed.
   */
  seedTopicId?: number | null;
  onCreated: (result: MakePagesResult) => void;
}

export default function MakePagesDialog({
  open,
  onClose,
  target,
  selectedCount,
  scopeLabel,
  seedTopicId,
  onCreated,
}: Props) {
  const [managedSites, setManagedSites] = useState<ManagedSite[]>([]);
  const [deploymentPlans, setDeploymentPlans] = useState<DeploymentPlan[]>([]);
  const [createManagedSiteId, setCreateManagedSiteId] = useState<number | null>(null);
  const [createPageStyle, setCreatePageStyle] = useState<PageStyle | ''>('');
  const [createDeploymentPlanId, setCreateDeploymentPlanId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [path, setPath] = useState<string[]>([]);
  const [seedDisplay, setSeedDisplay] = useState('');
  // Whether the path was EDITED, not merely shown. Sending it stamps
  // path_source="user", which stops a later rebuild refiling the page — so
  // showing someone the seed must not silently convert it into their choice.
  const [pathTouched, setPathTouched] = useState(false);
  const [creatingNewSite, setCreatingNewSite] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteRegisteredName, setNewSiteRegisteredName] = useState('');
  const [savingNewSite, setSavingNewSite] = useState(false);
  const [creatingNewPlan, setCreatingNewPlan] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [savingNewPlan, setSavingNewPlan] = useState(false);

  // Reset on every open, and load the deployment targets once we are shown.
  // '' = unset, so the Angle phase chooses the style from the SERP it has
  // actually seen. An earlier version reset this to ARTICLE, which re-pinned
  // the default on every open no matter what the caller intended.
  useEffect(() => {
    if (!open) return;
    setError('');
    setCreatePageStyle('');
    setCreateManagedSiteId(null);
    setCreateDeploymentPlanId(null);
    setDeploymentPlans([]);
    setPath([]);
    setSeedDisplay('');
    setPathTouched(false);
    loadSites();
    loadSeedPath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedTopicId]);

  /** The path this run would get if nobody touches it. */
  const loadSeedPath = async () => {
    if (!seedTopicId) return;
    try {
      const seed = await api.getTopicPagePath(seedTopicId);
      setPath(seed.segments);
      setSeedDisplay(seed.display);
    } catch {
      // An unreachable seed is not worth blocking the dialog for — the path
      // can be typed, and leaving it untouched still lets the server seed it.
    }
  };

  // Deployment targets are per-user, not per-project.
  const loadSites = async () => {
    try {
      setManagedSites(await api.getManagedSites());
    } catch (err) {
      console.error('Failed to load Deployment Targets:', err);
    }
  };

  const handleSiteChange = async (siteId: number | null) => {
    setCreateManagedSiteId(siteId);
    setCreateDeploymentPlanId(null);
    setDeploymentPlans([]);
    if (!siteId) return;
    try {
      const data = await api.getDeploymentPlans({ managedSiteId: siteId, size: 100 });
      setDeploymentPlans(data.items);
    } catch (err) {
      console.error('Failed to load deployment plans:', err);
    }
  };

  const handleCreateNewSite = async () => {
    if (!newSiteName.trim() || !newSiteRegisteredName.trim()) return;
    setSavingNewSite(true);
    setError('');
    try {
      const site = await api.createManagedSite({ name: newSiteName.trim(), registered_name: newSiteRegisteredName.trim() });
      setManagedSites(prev => [...prev, site]);
      await handleSiteChange(site.id);
      setCreatingNewSite(false);
      setNewSiteName('');
      setNewSiteRegisteredName('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setSavingNewSite(false);
    }
  };

  const handleCreateNewPlan = async () => {
    if (!newPlanName.trim() || !createManagedSiteId) return;
    setSavingNewPlan(true);
    setError('');
    try {
      const plan = await api.createDeploymentPlan({ name: newPlanName.trim(), managed_site_id: createManagedSiteId });
      setDeploymentPlans(prev => [...prev, plan]);
      setCreateDeploymentPlanId(plan.id);
      setCreatingNewPlan(false);
      setNewPlanName('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create deployment plan');
    } finally {
      setSavingNewPlan(false);
    }
  };

  const selectedSiteType =
    managedSites.find((s) => s.id === createManagedSiteId)?.site_type ?? null;

  const handleSubmit = async () => {
    if (selectedCount === 0) return;
    setCreating(true);
    setError('');
    try {
      const result =
        target.kind === 'filter'
          ? await api.bulkCreateManagedPagesByFilter({
              topic_id: target.topicId,
              // undefined = omit the field, so the backend leaves
              // page_style_source NULL and the Angle phase owns the choice.
              page_style: createPageStyle || undefined,
              deployment_plan_id: createDeploymentPlanId || NULL_REF_ID,
              keyword_search: target.keywordSearch || undefined,
              semantic_search: target.semanticSearch || undefined,
              min_sem_similarity: target.semanticSearch ? target.minSemSimilarity : undefined,
              // Untouched = omit, so the server seeds it and marks it "build".
              path_segments: pathTouched ? path : undefined,
            })
          : await api.bulkCreateManagedPages({
              topic_keyword_ids: target.ids,
              page_style: createPageStyle || undefined,
              deployment_plan_id: createDeploymentPlanId || NULL_REF_ID,
              path_segments: pathTouched ? path : undefined,
            });
      onClose();
      onCreated(result as MakePagesResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create pages');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => {
        onClose();
        setCreatingNewSite(false);
        setNewSiteName('');
        setNewSiteRegisteredName('');
        setCreatingNewPlan(false);
        setNewPlanName('');
      }}
      title={runTitle(selectedCount)}
    >
      <div>
        <div className="space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          {/* WHAT gets made. The count carries the shape of the run — one page
              per keyword reads differently from one report from all of them —
              without naming it. Only one format is registered today, so the
              chooser is a statement; it becomes a radio group when the second
              one lands. */}
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
              What
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-200">
              <span className="font-medium">{RUN_FORMATS[0].label}</span>
              <span className="text-gray-500 dark:text-gray-400"> - {RUN_FORMATS[0].blurb}</span>
              <span className="text-gray-500 dark:text-gray-400"> · </span>
              <span className="font-medium">{runOutcome(RUN_FORMATS[0], selectedCount)}</span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Page Style Override
            </label>
            <select
              value={createPageStyle}
              onChange={(e) => setCreatePageStyle(e.target.value as PageStyle | '')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Unset - let the Angle phase decide</option>
              {PAGE_STYLES.map((style) => (
                <option key={style.value} value={style.value}>
                  {style.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {createPageStyle
                ? 'Pins this style on every page created here. The Angle phase will not change it.'
                : "Each page's Angle phase picks the style from its own SERP - the step that has actually seen which page types rank."}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Managed Site <span className="font-normal text-gray-400 dark:text-gray-500">(optional - for deployment plan)</span>
            </label>
            {creatingNewSite ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSiteName}
                    onChange={e => setNewSiteName(e.target.value)}
                    placeholder="Site name"
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    autoFocus
                  />
                  <input
                    type="text"
                    value={newSiteRegisteredName}
                    onChange={e => setNewSiteRegisteredName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateNewSite()}
                    placeholder="Domain (e.g. example.com)"
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleCreateNewSite} disabled={savingNewSite || !newSiteName.trim() || !newSiteRegisteredName.trim()} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    {savingNewSite ? 'Creating...' : 'Create'}
                  </button>
                  <button onClick={() => { setCreatingNewSite(false); setNewSiteName(''); setNewSiteRegisteredName(''); }} className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  value={createManagedSiteId || ''}
                  onChange={(e) => handleSiteChange(e.target.value ? Number(e.target.value) : null)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No site selected</option>
                  {managedSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name} ({site.registered_name})
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setCreatingNewSite(true)}
                  className="px-2 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 whitespace-nowrap"
                >
                  + New
                </button>
              </div>
            )}
          </div>

          {createManagedSiteId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Deployment Plan <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
              </label>
              {creatingNewPlan ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPlanName}
                    onChange={e => setNewPlanName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateNewPlan()}
                    placeholder="Plan name"
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    autoFocus
                  />
                  <button onClick={handleCreateNewPlan} disabled={savingNewPlan || !newPlanName.trim()} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    {savingNewPlan ? 'Creating...' : 'Create'}
                  </button>
                  <button onClick={() => { setCreatingNewPlan(false); setNewPlanName(''); }} className="px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <select
                    value={createDeploymentPlanId || ''}
                    onChange={(e) => setCreateDeploymentPlanId(e.target.value ? Number(e.target.value) : null)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No plan selected</option>
                    {deploymentPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setCreatingNewPlan(true)}
                    className="px-2 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 whitespace-nowrap"
                  >
                    + New
                  </button>
                </div>
              )}
            </div>
          )}

          {/* AFTER the site, because the path only means anything once there is
              a site to publish to — and what a path DOES depends on the site's
              type. Seeded from the node so the common case is "looks right,
              carry on"; editable because the outline and the site are allowed
              to disagree, which is the entire point of storing this per page. */}
          {createManagedSiteId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Publish path{' '}
                <span className="font-normal text-gray-400 dark:text-gray-500">
                  {selectedCount > 1 ? `(applies to all ${selectedCount})` : '(optional)'}
                </span>
              </label>
              <PathChips
                segments={path}
                onChange={(next) => {
                  setPath(next);
                  setPathTouched(true);
                }}
                managedSiteId={createManagedSiteId}
                previewSlug={selectedCount > 1 ? 'each-page-slug' : 'page-slug'}
                nested={isNestedSiteType(selectedSiteType)}
                seededFrom={pathTouched ? undefined : seedDisplay}
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => { onClose(); setCreatingNewSite(false); setNewSiteName(''); setNewSiteRegisteredName(''); setCreatingNewPlan(false); setNewPlanName(''); }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={creating}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {creating ? 'Making…' : runButtonLabel(RUN_FORMATS[0], selectedCount)}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
